import { differenceInMinutes, format } from 'date-fns';
import { Pressable, StyleSheet, View } from 'react-native';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import {
  EventSourceIndicator,
  useEventSourceAccessibilityLabel,
} from '../indicators/EventSourceIndicator';
import { type EventStatusTone, getEventAppearance } from '../utils/calendarEventAppearance';
import { JoinCallButton } from './JoinCallButton';

interface CalendarAgendaRowProps {
  event: CalendarEvent;
  onPress?: (event: CalendarEvent) => void;
}

const STATUS_TONE_COLORS: Record<Exclude<EventStatusTone, 'none'>, 'green' | 'red' | 'neutral'> = {
  success: 'green',
  error: 'red',
  muted: 'neutral',
};

/**
 * One meeting in the agenda list.
 *
 * Unlike a grid block, this row FLOWS — its height comes from its content, not
 * from the meeting's duration — so nothing here is dropped for want of space
 * and nothing clips when the viewer scales their text up.
 */
export function CalendarAgendaRow({ event, onPress }: Readonly<CalendarAgendaRowProps>) {
  const { t } = useTranslation();
  const sourceLabel = useEventSourceAccessibilityLabel(event.source);

  const startsAt = new Date(event.startsAt);
  const endsAt = new Date(event.endsAt);
  const appearance = getEventAppearance(
    event.source,
    event.status,
    differenceInMinutes(endsAt, startsAt),
  );
  const statusLabel = appearance.statusLabelKey ? t(appearance.statusLabelKey) : null;

  const accessibilityLabel = [
    event.title,
    event.allDay
      ? t('calendar.agenda.allDay')
      : `${format(startsAt, 'h:mm a')} to ${format(endsAt, 'h:mm a')}`,
    sourceLabel,
    statusLabel,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Pressable
      testID={`calendar_agenda_row_${event.id}`}
      // The row contains its own Join button. Claiming the whole subtree here
      // would make iOS VoiceOver flatten it and swallow that button, so the
      // row exposes a label on its body instead and leaves the button its own
      // node. `accessibilityActions` keeps the row itself operable.
      accessible={false}
      onPress={() => onPress?.(event)}
      style={styles.row}
    >
      <Box
        style={[
          styles.surface,
          { backgroundColor: appearance.fill, borderColor: appearance.borderColor },
        ]}
        padding="md"
      >
        <HStack spacing="md" align="start">
          <VStack
            spacing="none"
            style={styles.time}
            accessible
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
          >
            {event.allDay ? (
              <Text variant="caption" color={appearance.metaColor}>
                {t('calendar.agenda.allDay')}
              </Text>
            ) : (
              <>
                <Text variant="bodySmall" color={appearance.titleColor} style={styles.startTime}>
                  {format(startsAt, 'h:mm a')}
                </Text>
                <Text variant="caption" color={appearance.metaColor} style={styles.tabular}>
                  {format(endsAt, 'h:mm a')}
                </Text>
              </>
            )}
          </VStack>

          <View style={[styles.rail, { backgroundColor: appearance.borderColor }]} />

          <VStack spacing="xs" style={styles.body}>
            <Text
              testID={`calendar_agenda_row_${event.id}_title`}
              variant="bodySmall"
              numberOfLines={2}
              color={appearance.titleColor}
              style={[
                styles.title,
                appearance.strikeThrough && { textDecorationLine: 'line-through' },
              ]}
            >
              {event.title}
            </Text>

            <HStack spacing="xs" align="center" wrap>
              {/* Labelled here, not icon-only: a flowing row has the width for
                  the word, and the word is what keeps source off colour alone. */}
              <EventSourceIndicator
                source={event.source}
                size="sm"
                testID={`calendar_agenda_row_${event.id}_source`}
              />
              {statusLabel && appearance.statusTone !== 'none' && (
                <Badge
                  testID={`calendar_agenda_row_${event.id}_status`}
                  variant="subtle"
                  color={STATUS_TONE_COLORS[appearance.statusTone]}
                  size="sm"
                >
                  {statusLabel}
                </Badge>
              )}
            </HStack>

            <JoinCallButton conferenceUrl={event.conferenceUrl} eventId={event.id} />
          </VStack>
        </HStack>
      </Box>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    minHeight: tokens.interaction.minimumTarget.default,
  },
  surface: {
    borderWidth: 1,
    borderRadius: tokens.radius.lg,
  },
  time: {
    width: 64,
  },
  startTime: {
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  tabular: {
    fontVariant: ['tabular-nums'],
  },
  rail: {
    width: 3,
    borderRadius: tokens.radius.sm,
    alignSelf: 'stretch',
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: '600',
  },
});
